import { useCallback, useEffect, useRef, useState } from 'react'

export type AreasPhase = 'loading' | 'ready' | 'error'

export type AreaType = 'hunt' | 'sub' | 'forbidden'

export interface Area {
  id: string
  areaType: AreaType
  name: string | null
  // [longitude, latitude] pairs, already in GeoJSON/MapLibre order — the API
  // does the lat/lon swap server-side so nothing downstream has to.
  coordinates: [number, number][]
  // The owning group's id. `areas` arrives already in drawing order, bottom
  // to top, with every area of one (groupId, areaType) pair contiguous —
  // MapView's groupAreasByLayer reconstructs the layer stack from that
  // ordering rather than this app re-deriving one of its own.
  groupId: string
  // Shared by every area of the same (groupId, areaType) pair. Sent per-area
  // rather than once per pair so a reader never has to reach into a sibling
  // area to find out how to draw this one.
  color: string
  width: number
}

export interface Line {
  id: string
  name: string | null
  // "#RRGGBB", already converted server-side from Firestore's packed ARGB32.
  color: string
  coordinates: [number, number][]
}

interface AreasResponse {
  areas: Area[]
  lines: Line[]
}

type Call = <T>(path: string) => Promise<T>

// Mirrors useBaseMap's shape exactly, including the latest-ref pattern below.
// The call is injected rather than resolved from context so the hook can be
// driven directly in a test without mounting an auth provider.
export function useAreas(call: Call) {
  const [phase, setPhase] = useState<AreasPhase>('loading')
  const [areas, setAreas] = useState<Area[]>([])
  const [lines, setLines] = useState<Line[]>([])
  const [attempt, setAttempt] = useState(0)

  // `call` is not a dependency of the effect below. useApiFetch's returned
  // function is a useCallback keyed on live auth state, so its identity can
  // change between renders; if the effect re-ran on every such change it
  // would restart the in-flight request each time phase settles and
  // re-renders the component, which recomputes `call` again — a
  // loading/settled loop that never lets the phase stick. Keying the effect
  // on `attempt` alone, and always reading the latest `call` through this
  // ref, decouples "when to fetch" (mount + explicit retry) from "which
  // function instance to call" (always the newest one).
  const callRef = useRef(call)
  callRef.current = call

  useEffect(() => {
    let cancelled = false
    setPhase('loading')
    callRef.current<AreasResponse>('/areas')
      .then((res) => {
        if (!cancelled) {
          setAreas(res.areas)
          setLines(res.lines)
          setPhase('ready')
        }
      })
      .catch(() => {
        // Borders are supplementary to the heatmap, not core to the screen:
        // an 'error' phase here must never stop MapScreen from rendering the
        // map, unlike useBaseMap's, which does gate the map. The caller is
        // expected to just leave areas/lines at their empty defaults.
        if (!cancelled) setPhase('error')
      })
    return () => {
      cancelled = true
    }
  }, [attempt])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])
  return { phase, areas, lines, retry }
}
